import { call, put, select } from 'typed-redux-saga'
import { all, retry } from 'redux-saga/effects'

import { getExchangeToken } from 'APP/BrownfieldBridge/BrownfieldBridgeInternal'

// Actions
import AccountLinkingActions from 'APP/Redux/AccountLinkingRedux'
import LoginActions from 'APP/Redux/LoginRedux'

// Services
import { getMemberSSOApi } from 'APP/Store/utils/multipass'

// Sagas
import { loginState, logout, checkRenewAuth, refreshAuth, processLoginSuccess } from './AuthSagas'
import { fetchScribeV2User, fetchScribeV2UserEligibilities } from './PatientSagas'
import { logger } from 'APP/Lib/Datadog'

const RETRY_LIMIT = 3
const DELAY_TIME = 1500

interface RequestAccountLinkingAction {
  accountCreation?: boolean
}

export function* requestAccountLinking(action: RequestAccountLinkingAction = {}) {
  // If accessToken is expired request new one
  yield* call(checkRenewAuth)

  const { accountCreation } = action
  const login = yield* select(loginState)
  const api = yield* call(getMemberSSOApi)

  const {
    refreshToken,
    secondaryAccessToken,
    exchangeToken,
    customClaims: { sso_link_status: ssoLinkStatus, 'sso-connection': claimConnection },
  } = login

  // Request linking
  try {
    const access_token = !accountCreation ? secondaryAccessToken : undefined

    yield retry(RETRY_LIMIT, DELAY_TIME, api.selfUsersMeLinkPost, {
      bodyLinkSelfUsersMeLinkPost: { access_token },
    })
  } catch (e) {
    const stringify = JSON.stringify(e)
    const errorType = accountCreation ? 'creation' : 'linking'
    yield* put(AccountLinkingActions.accountLinkingFailure(stringify, errorType))
    logger.error('Failed to link account', { errorType }, e)
    return
  }

  yield* put(LoginActions.clearSecondaryAccessToken())
  yield* put(LoginActions.clearConnection())

  if (accountCreation) {
    // It is required for the user to re-give explicit consent, which means we cannot log them in silently.
    // https://auth0.com/docs/authenticate/login/configure-silent-authentication#error-responses
    // By using the connection in the SSO claim, auth0 bypasses some of the auth steps for the user.

    if (exchangeToken) {
      let refreshedExcToken

      try {
        refreshedExcToken = yield* call(getExchangeToken)
      } catch (_) {
        // Default to current token in state (this will most likely be expired, but it
        // allows the flow to continue and have the login function handle the error
        // case consistently)
        refreshedExcToken = exchangeToken
      }

      yield* put(LoginActions.loginRequestWithExchangeToken(refreshedExcToken))
    } else {
      yield* put(LoginActions.silentLoginRequestWithConnection(claimConnection))
    }

    yield* put(AccountLinkingActions.accountLinkingSuccess())

    return
  }

  yield* put(AccountLinkingActions.accountLinkingSuccess())

  // Request token refresh
  try {
    yield* call(refreshAuth, refreshToken)
  } catch (e) {
    logger.error('Failed to refresh token', {}, e)
    yield* call(logout, { reason: 'Error requesting token refresh ' })
    return
  }

  try {
    yield all([call(fetchScribeV2User), call(fetchScribeV2UserEligibilities)])
    yield* call(processLoginSuccess, ssoLinkStatus, true)
  } catch (e) {
    logger.error('Failed to process successful login after account linking', {}, e)
    yield* put(
      LoginActions.loginFailure({
        ...(e as Error),
        message: (e as Error).message,
        cause: (e as Error & { cause?: unknown }).cause,
      })
    )
  }
}
